import { ProgressBar, ProgressCircle, Tag } from 'antd-mobile';
import { formatThousandAndUnit, keepOneOrLine, keepOnePercentOrLine } from '@/utils/utils';
import { EnergyUsageResponse, TimeResolution } from '@/api/energyOverview';
import styles from './index.module.scss';
import electricImg from '../../images/icon-electric@2x.png';
import waterImg from '../../images/icon-water@2x.png';
import gasImg from '../../images/icon-gas@2x.png';
import { colors } from '../EnergyCost/options';

interface EnergyUsageProps {
  energyUsageData?: EnergyUsageResponse;
  dimension: TimeResolution;
}

const EnergyUsage = ({ energyUsageData = {} }: EnergyUsageProps) => {
  // 安全访问数据
  const { electricityData, waterData, gasData, plan, actual, ratio } = energyUsageData;
  // 效益=中水实际用量/(自来水实际用量+中水实际用量）*100%；
  const waterBenefit =
    waterData?.middleWater?.actual && waterData?.freshWater?.actual
      ? waterData?.middleWater?.actual / (waterData?.freshWater?.actual + waterData?.middleWater?.actual)
      : 0;
  return (
    <div className={styles.usageSection}>
      {/* 能源用量概览 */}
      <div className={styles.usagePanel}>
        <div className={styles.sectionTitle}>能源用量</div>
        <div className={styles.usageSummary}>
          {/* 左侧：计划用量和实际用量 */}
          <div className={styles.usageLeft}>
            <div className={styles.usageRow}>
              <span className={styles.usageLabel}>计划标准煤：</span>
              <span className={styles.usageValue}>
                <span className={styles.unit}>{formatThousandAndUnit(plan).value}</span>
                <span>{formatThousandAndUnit(plan).unit}</span>kgce
              </span>
            </div>
            <div className={styles.usageRow}>
              <span className={styles.usageLabel}>实际标准煤：</span>
              <span className={styles.usageValue}>
                <span className={styles.unit}>{formatThousandAndUnit(actual).value}</span>
                <span>{formatThousandAndUnit(actual).unit}</span>kgce
              </span>
            </div>
          </div>
          {/* 右侧：占比和进度条 */}
          <div className={styles.usageRight}>
            <div className={styles.progressRow}>
              <ProgressBar
                percent={Math.min(100, Math.abs(ratio || 0) * 100)}
                style={{
                  margin: '8px 0',
                  '--fill-color': 'linear-gradient(to right, #CDFFF8, #A0E4FF)',
                  '--track-color': 'rgba(255,255,255,0.2)',
                }}
              />
            </div>
            <div className={styles.percentRow}>
              <span>占比(实际/计划)：</span>
              <span className={styles.percentValue}>{keepOneOrLine(ratio)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* 用电量模块 */}
      <div className={styles.usageModule}>
        <div className={styles.moduleHeader}>
          <div className={styles.moduleTitle}>
            <img src={electricImg} width={16} alt="" />
            用电量统计
          </div>
        </div>
        <div className={styles.moduleContent}>
          <div className={styles.topArea}>
            {/* 左侧：占比进度条 + 电能数据 */}
            <div className={styles.leftArea}>
              <div className={styles.progressWrap}>
                <ProgressBar
                  percent={Math.min(100, Math.abs(electricityData?.usageData?.ratio || 0) * 100)}
                  style={{
                    padding: '4px 0',
                    '--fill-color': 'linear-gradient(to right, #31AFFF, #1677FF)',
                    '--track-color': '#E3EFFF',
                  }}
                />
              </div>
              <div className={styles.dataRow}>
                <span className={styles.dataLabel}>电能实际用量：</span>
                <span className={styles.dataValue}>
                  {formatThousandAndUnit(electricityData?.usageData?.actual).value}
                  {formatThousandAndUnit(electricityData?.usageData?.actual).unit}kWh
                </span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.dataLabel}>本年计划用量：</span>
                <span className={styles.dataValue}>
                  {formatThousandAndUnit(electricityData?.usageData?.plan).value}
                  {formatThousandAndUnit(electricityData?.usageData?.plan).unit}kWh
                </span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.dataLabel}>占比(实际/计划)：</span>
                <span className={styles.ratio}>{keepOneOrLine(electricityData?.usageData?.ratio)}%</span>
              </div>
            </div>
            {/* 右侧电量详情 */}
            <div className={styles.detailArea}>
              <div className={styles.periodList}>
                <div className={styles.periodItem}>
                  <div className={styles.periodName}>
                    <span className={styles.dot} style={{ background: colors[0] }} />
                    尖时段
                  </div>
                  <span className={styles.periodValue}>
                    {formatThousandAndUnit(electricityData?.summit?.value).value}
                    {formatThousandAndUnit(electricityData?.summit?.value).unit}kWh
                  </span>
                  <span className={styles.periodRatio}>{keepOnePercentOrLine(electricityData?.summit?.ratio)}%</span>
                </div>
                <div className={styles.periodItem}>
                  <div className={styles.periodName}>
                    <span className={styles.dot} style={{ background: colors[1] }} />
                    峰时段
                  </div>
                  <span className={styles.periodValue}>
                    {formatThousandAndUnit(electricityData?.peak?.value).value}
                    {formatThousandAndUnit(electricityData?.peak?.value).unit}kWh
                  </span>
                  <span className={styles.periodRatio}>{keepOnePercentOrLine(electricityData?.peak?.ratio)}%</span>
                </div>
                <div className={styles.periodItem}>
                  <div className={styles.periodName}>
                    <span className={styles.dot} style={{ background: colors[2] }} />
                    平时段
                  </div>
                  <span className={styles.periodValue}>
                    {formatThousandAndUnit(electricityData?.plain?.value).value}
                    {formatThousandAndUnit(electricityData?.plain?.value).unit}kWh
                  </span>
                  <span className={styles.periodRatio}>{keepOnePercentOrLine(electricityData?.plain?.ratio)}%</span>
                </div>
                <div className={styles.periodItem}>
                  <div className={styles.periodName}>
                    <span className={styles.dot} style={{ background: colors[3] }} />
                    谷时段
                  </div>
                  <span className={styles.periodValue}>
                    {formatThousandAndUnit(electricityData?.valley?.value).value}
                    {formatThousandAndUnit(electricityData?.valley?.value).unit}kWh
                  </span>
                  <span className={styles.periodRatio}>{keepOnePercentOrLine(electricityData?.valley?.ratio)}%</span>
                </div>
              </div>
            </div>
          </div>
          {/* 底部：光伏发电总量 + 绿电占比 */}
          <div className={styles.bottomArea}>
            <div className={styles.bottomItem}>
              <span className={styles.bottomLabel}>光伏发电总量：</span>
              <span className={styles.bottomValue}>
                {formatThousandAndUnit(electricityData?.value).value}
                {formatThousandAndUnit(electricityData?.value).unit}kWh
              </span>
            </div>
            <div className={styles.bottomItem}>
              <span className={styles.bottomLabel}>绿电占比；</span>
              <div className={styles.greenProgressWrap}>
                <span className={styles.bottomValue}>{keepOnePercentOrLine(electricityData?.greenRatio)}%</span>
              </div>
              <ProgressCircle
                percent={Math.min(100, Math.abs(electricityData?.greenRatio || 0) * 100)}
                style={{
                  '--size': '28px',
                  '--track-width': '4px',
                  '--fill-color': '#00B578',
                  '--track-color': '#E3EFFF',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 用水量模块 */}
      <div className={styles.usageModule}>
        <div className={styles.moduleHeader}>
          <div className={styles.moduleTitle}>
            <img src={waterImg} width={16} alt="" />
            用水量统计
          </div>
        </div>
        <div className={styles.waterModuleContent}>
          <div className={styles.row}>
            <div className={styles.item}>
              <span>自来水实际用量</span>
              <span className={styles.value}>
                {formatThousandAndUnit(waterData?.freshWater?.actual).value}
                {formatThousandAndUnit(waterData?.freshWater?.actual).unit}m³
              </span>
            </div>
            <div className={styles.item}>
              <span>本年计划用量</span>
              <span className={styles.value}>
                {formatThousandAndUnit(waterData?.freshWater?.plan).value}
                {formatThousandAndUnit(waterData?.freshWater?.plan).unit}m³
              </span>
            </div>
            <div className={styles.ratioWrap}>
              <span className={styles.ratioValue}>{keepOneOrLine(waterData?.freshWater?.ratio)}%</span>
              <ProgressCircle
                percent={Math.min(100, Math.abs(waterData?.freshWater?.ratio || 0))}
                style={{
                  '--size': '28px',
                  '--track-width': '4px',
                  '--fill-color': '#1677ff',
                  '--track-color': '#E3EFFF',
                }}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.item}>
              <span>中水实际用量</span>
              <span className={styles.value}>
                {formatThousandAndUnit(waterData?.middleWater?.actual).value}
                {formatThousandAndUnit(waterData?.middleWater?.actual).unit}m³
              </span>
            </div>
            <div className={styles.item}>
              <span>本年计划用量</span>
              <span className={styles.value}>
                {formatThousandAndUnit(waterData?.middleWater?.plan).value}
                {formatThousandAndUnit(waterData?.middleWater?.plan).unit}m³
              </span>
            </div>
            <div className={styles.ratioWrap}>
              <span className={styles.benefit}>
                <span>中水效益</span>
                <Tag
                  style={{
                    width: 'fit-content',
                    '--background-color': '#E3EFFF',
                    '--text-color': '#1677FF',
                    '--border-color': '#E3EFFF',
                  }}
                >
                  {keepOnePercentOrLine(waterBenefit)}%
                </Tag>
              </span>
              <span className={styles.ratioValue}>{keepOneOrLine(waterData?.middleWater?.ratio)}%</span>
              <ProgressCircle
                percent={Math.min(100, Math.abs(waterData?.middleWater?.ratio || 0))}
                style={{
                  '--size': '28px',
                  '--track-width': '4px',
                  '--fill-color': '#1677ff',
                  '--track-color': '#E3EFFF',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 用气量模块 */}
      <div className={styles.usageModule}>
        <div className={styles.moduleHeader}>
          <div className={styles.moduleTitle}>
            <img src={gasImg} width={16} alt="" />
            用气量统计
          </div>
        </div>
        <div className={styles.gasModuleContent}>
          {gasData?.map((item, index) => {
            const isLast = index === (gasData?.length || 0) - 1;
            return (
              <div key={item.energyMediumId} className={`${styles.row} ${isLast ? styles.noBorder : ''}`}>
                <div className={styles.item}>
                  <span>{item.energyMediumName}实际用量</span>
                  <span className={styles.value}>
                    {formatThousandAndUnit(item.actual).value}
                    {formatThousandAndUnit(item.actual).unit}m³
                  </span>
                </div>
                <div className={styles.item}>
                  <span>本年计划用量</span>
                  <span className={styles.value}>
                    {formatThousandAndUnit(item.plan).value}
                    {formatThousandAndUnit(item.plan).unit}m³
                  </span>
                </div>
                <div className={styles.ratioWrap}>
                  <span className={styles.ratioValue}>{keepOneOrLine(item?.ratio)}%</span>
                  <ProgressCircle
                    percent={Math.min(100, Math.abs(item?.ratio || 0))}
                    style={{
                      '--size': '28px',
                      '--track-width': '4px',
                      '--fill-color': '#1677ff',
                      '--track-color': '#E3EFFF',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EnergyUsage;
